"use client";

import { useCallback, useMemo } from "react";
import { EmptyPresetsIllustration } from "@/components/illustrations/empty-presets-illustration";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalClose,
} from "@/components/ui/modal";
import { Button, SubmitButton } from "@/components/ui/button";
import { ProgressAction } from "@/components/ui/progress-action";
import { UploadArea, useUploadArea } from "@/components/ui/upload-area";
import { SearchInput } from "@/components/ui/input";
import { logo } from "@/lib/logo-dev";
import { useCsvImport, type CsvImportResult } from "@/stores/csv-import-store";
import { createNameMatchResolver } from "@/lib/csv/resolvers";
import type { BulkMatchRequestField } from "@/lib/api/model";
import type { PresetTypeSlug } from "../lib/categories";
import { Icon } from "@/components/ui/icon";

const LOGO_TYPES: PresetTypeSlug[] = ["companies", "universities", "investors"];

interface PresetMeta {
  entityNoun: string;
  unmatchedLabel: string;
  sampleCsv: string;
  sampleFilename: string;
  description: string;
  showLogos: boolean;
  autocompleteField: string;
}

function getPresetMeta(presetType: PresetTypeSlug): PresetMeta {
  const singular = (noun: string) =>
    noun.endsWith("ies")
      ? noun.slice(0, -3) + "y"
      : noun.endsWith("s")
        ? noun.slice(0, -1)
        : noun;

  const base: Record<
    Exclude<PresetTypeSlug, "filters">,
    { noun: string; field: string }
  > = {
    companies: { noun: "companies", field: "companies" },
    universities: { noun: "universities", field: "universities" },
    "job-titles": { noun: "job titles", field: "job_titles" },
    skills: { noun: "skills", field: "skills" },
    investors: { noun: "investors", field: "companies" },
    locations: { noun: "locations", field: "locations" },
    "job-stages": { noun: "stages", field: "job_titles" },
  };

  const entry = base[presetType as keyof typeof base] ?? {
    noun: "items",
    field: "companies",
  };
  const sing = singular(entry.noun);

  return {
    entityNoun: entry.noun,
    unmatchedLabel: `${sing.charAt(0).toUpperCase() + sing.slice(1)} not found`,
    sampleCsv: `name\nGoogle\nMicrosoft\nApple\nAmazon\nMeta`,
    sampleFilename: `sample-${entry.noun.replace(/\s/g, "-")}.csv`,
    description: `Drag & drop or upload a CSV file with one column containing the name of the ${sing}`,
    showLogos: LOGO_TYPES.includes(presetType),
    autocompleteField: entry.field,
  };
}

export interface ImportPresetCsvProps {
  open: boolean;
  onClose: () => void;
  onImport: (entities: CsvImportResult[]) => void;
  presetType: PresetTypeSlug;
}

function UploadZoneBody({
  description,
  sampleCsv,
  sampleFilename,
}: {
  description: string;
  sampleCsv: string;
  sampleFilename: string;
}) {
  const { isDragOver } = useUploadArea();

  const onDownloadSample = useCallback(() => {
    const blob = new Blob([sampleCsv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = sampleFilename;
    a.click();
    URL.revokeObjectURL(url);
  }, [sampleCsv, sampleFilename]);

  if (isDragOver) {
    return (
      <UploadArea.Container>
        <UploadArea.Dropping>Drop CSV file here</UploadArea.Dropping>
      </UploadArea.Container>
    );
  }

  return (
    <UploadArea.Container>
      <UploadArea.Icon>
        <EmptyPresetsIllustration
          height={50}
          width={75}
          className="h-[50px] w-[75px]"
        />
      </UploadArea.Icon>
      <UploadArea.Actions>
        <UploadArea.Action
          icon={
            <Icon
              name="IconNewspaper1"
              size={14}
              aria-hidden="true"
              className="text-heading opacity-60"
            />
          }
        >
          Upload CSV File
        </UploadArea.Action>
        <Button
          variant="secondary"
          size="sm"
          onClick={onDownloadSample}
          icon={
            <Icon
              name="IconFileDownload"
              size={14}
              aria-hidden="true"
              className="text-heading opacity-60"
            />
          }
        >
          Download Sample CSV
        </Button>
      </UploadArea.Actions>
      <UploadArea.Content>{description}</UploadArea.Content>
      <UploadArea.Limits>*.csv files up to 5MB</UploadArea.Limits>
    </UploadArea.Container>
  );
}

export function ImportPresetCsv({
  open,
  onClose,
  onImport,
  presetType,
}: ImportPresetCsvProps) {
  const csv = useCsvImport();
  const meta = useMemo(() => getPresetMeta(presetType), [presetType]);

  const resolver = useMemo(
    () =>
      createNameMatchResolver(
        meta.autocompleteField as BulkMatchRequestField,
      ),
    [meta.autocompleteField],
  );

  const handleReset = () => {
    csv.reset();
    onClose();
  };

  const handleImport = () => {
    onImport(csv.getSelected());
    csv.reset();
    onClose();
  };

  const label =
    meta.entityNoun.charAt(0).toUpperCase() + meta.entityNoun.slice(1);

  return (
    <Modal open={open} onOpenChange={(o) => !o && handleReset()}>
      <ModalContent size="md">
        <ModalHeader
          title={`Import ${label} from CSV`}
          iconName="IconLayoutDashboard"
        />

        <ModalBody className="flex flex-col gap-5 max-h-[70vh] overflow-x-hidden">
          <UploadArea.Provider
            accept=".csv,text/csv"
            disabled={csv.isProcessing}
            onSelect={(files) => {
              const f = files[0];
              if (f) csv.processFile(f, resolver);
            }}
          >
            <UploadZoneBody
              description={meta.description}
              sampleCsv={meta.sampleCsv}
              sampleFilename={meta.sampleFilename}
            />
          </UploadArea.Provider>

          {csv.entities.length > 0 || csv.isProcessing ? (
            <UploadArea.ResultsContainer>
              <UploadArea.ResultsToolbar>
                <SearchInput
                  value={csv.search}
                  onChange={(e) => {
                    csv.setSearch(e.target.value);
                    csv.setPage(1);
                  }}
                  placeholder="Search"
                  aria-label={`Search imported ${meta.entityNoun}`}
                  className="w-28 shrink-0 sm:w-40"
                />
                <div role="status" aria-live="polite" className="sr-only">
                  {csv.isProcessing
                    ? `Uploading ${meta.entityNoun}: ${csv.processedCount} of ${csv.totalParsed} processed.`
                    : `Imported ${csv.matchedCount} matched, ${csv.unmatchedCount} not found${
                        csv.duplicatesRemoved > 0
                          ? `, ${csv.duplicatesRemoved} duplicates removed`
                          : ""
                      }.`}
                </div>
                <ProgressAction>
                  <ProgressAction.StatusIcon
                    state={csv.isProcessing ? "processing" : "complete"}
                  />
                  <ProgressAction.Main>
                    <ProgressAction.Label>
                      {csv.isProcessing ? "Uploading" : "Imported"}
                    </ProgressAction.Label>
                    <ProgressAction.BarGroup>
                      <ProgressAction.Track>
                        <ProgressAction.Segment
                          value={csv.matchedPct}
                          className="bg-success"
                        />
                        <ProgressAction.Segment
                          value={csv.unmatchedPct}
                          className="bg-destructive"
                        />
                        <ProgressAction.Segment
                          value={csv.duplicatePct}
                          className="bg-ai"
                        />
                      </ProgressAction.Track>
                      {/* TODO(a11y AAA-contrast): tooltip text colors text-success, text-destructive, text-ai and text-contrast/40 likely fail SC 1.4.6 (7:1) on dark surfaces — also note the colored progress segments are decorative; the text duplicate above carries the meaning. */}
                      {(csv.entities.length > 0 ||
                        csv.duplicatesRemoved > 0) && (
                        <ProgressAction.Tooltip>
                          <span className="text-success">
                            {csv.matchedCount} matched
                          </span>
                          <span className="text-contrast/40 mx-1">&middot;</span>
                          <span className="text-destructive">
                            {csv.unmatchedCount} not found
                          </span>
                          {csv.duplicatesRemoved > 0 && (
                            <>
                              <span className="text-contrast/40 mx-1">
                                &middot;
                              </span>
                              <span className="text-ai">
                                {csv.duplicatesRemoved} duplicates removed
                              </span>
                            </>
                          )}
                        </ProgressAction.Tooltip>
                      )}
                    </ProgressAction.BarGroup>
                    <ProgressAction.Fraction
                      current={csv.processedCount}
                      total={csv.totalParsed}
                    />
                  </ProgressAction.Main>
                </ProgressAction>
              </UploadArea.ResultsToolbar>

              <UploadArea.Results>
                {csv.pagedEntities.map((entity) => {
                  const entityLogoUrl =
                    meta.showLogos && entity.matched
                      ? logo({
                          domain: entity.domain,
                          name: entity.name,
                          size: 32,
                        })
                      : null;

                  return (
                    <UploadArea.ResultsRow
                      key={entity.id}
                      matched={entity.matched}
                    >
                      <UploadArea.ResultsRow.Start>
                        {entity.matched ? (
                          <UploadArea.ResultsRow.Select
                            selected={entity.selected}
                            onToggle={() => csv.toggleEntity(entity.id)}
                          />
                        ) : (
                          <UploadArea.ResultsRow.SelectSpacer />
                        )}
                        <UploadArea.ResultsRow.Main>
                          <UploadArea.ResultsRow.Avatar
                            src={entityLogoUrl}
                            alt=""
                          />
                          <UploadArea.ResultsRow.Label>
                            {entity.name}
                          </UploadArea.ResultsRow.Label>
                        </UploadArea.ResultsRow.Main>
                      </UploadArea.ResultsRow.Start>
                      <UploadArea.ResultsRow.End>
                        <UploadArea.ResultsRow.Badge
                          variant={entity.matched ? "success" : "danger"}
                        >
                          {entity.matched ? "Matched" : meta.unmatchedLabel}
                        </UploadArea.ResultsRow.Badge>
                      </UploadArea.ResultsRow.End>
                    </UploadArea.ResultsRow>
                  );
                })}
              </UploadArea.Results>

              <UploadArea.ResultsPagination
                page={csv.page}
                totalPages={csv.totalPages}
                onPageChange={csv.setPage}
              />
            </UploadArea.ResultsContainer>
          ) : null}
        </ModalBody>

        <ModalFooter align="end">
          <ModalClose asChild>
            <Button variant="secondary" size="sm" onClick={handleReset}>
              Cancel
            </Button>
          </ModalClose>
          {csv.entities.length > 0 && (
            <SubmitButton
              type="button"
              variant="primary"
              size="sm"
              onClick={handleImport}
              disabled={csv.selectedCount === 0}
              loading={false}
            >
              Import {csv.selectedCount} {label}
            </SubmitButton>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
